<script setup lang="ts">
  import { useFormatting } from '@gat-ship/utils'
  import { computed } from 'vue'
  import { testIds } from '@gat-ship/types'
  import {
    IconAdmin,
    IconCalendarClock,
    IconDeactivate,
    IconLoading
  } from '@gat-ship/icons'
  import {
    tenantUserLoadingIds,
    useTenantUserDetailContext
  } from '../composables/useTenantUserDetail'
  import { useTenantUserUtils } from '../composables/useTenantUserUtils'
  import { useTenantUserModals } from '../components/providers'
  import {
    Button,
    ContentRowTwoColumns,
    useAppLoading
  } from '@gat-ship/vue-components'
  const props = defineProps<{ id?: string }>()

  const { formatDateTime } = useFormatting()
  const tenantUserProvider = useTenantUserDetailContext()!
  const { TenantUserUtils } = useTenantUserUtils()
  const modals = useTenantUserModals()!
  const { isLoading: appIsLoading } = useAppLoading()

  const tenantUser = computed(() => {
    if (!props.id) {
      return undefined
    }
    return tenantUserProvider.tenantUser.value
  })

  const isActivating = computed(() =>
    appIsLoading([tenantUserLoadingIds.activateUser])
  )
  const isDeactivating = computed(() =>
    appIsLoading([tenantUserLoadingIds.deactivateUser])
  )
  const isSchedulingDeactivation = computed(() =>
    appIsLoading([tenantUserLoadingIds.scheduleDeactivation])
  )
  const isGranting = computed(() =>
    appIsLoading([tenantUserLoadingIds.grantTenantAdminPrivileges])
  )
  const isRevoking = computed(() =>
    appIsLoading([tenantUserLoadingIds.revokeTenantAdminPrivileges])
  )
</script>

<template>
  <div v-if="tenantUser" class="flex w-full flex-col gap-4">
    <ContentRowTwoColumns v-if="tenantUser?.canActivate" title="Activate User">
      <template #description>
        Gives the user instant access to the application.
        <!--  Activate the user immediately. -->
      </template>
      <div class="text-muted-foreground text-xs font-medium">
        Deactivated on {{ formatDateTime(tenantUser.activeTo) }}
      </div>
      <Button
        v-if="tenantUser.canActivate && !tenantUser.isActive"
        variant="outline"
        size="sm"
        class="w-fit"
        :disabled="isActivating"
        :data-testid="testIds.tenantUser.actions.activateUser"
        @click="modals.showActivate({ tenantUser })"
      >
        Activate User
      </Button>
    </ContentRowTwoColumns>
    <ContentRowTwoColumns
      v-if="tenantUser?.canDeactivate"
      title="Deactivate User"
    >
      <template #description>
        Deactivate this account to immediately block the user's access. The
        account and data remain intact and can be reactivated later if needed.
        <!-- The user will no longer have access to gat-ship effective immediately.
        <br /><br />
        The user will be logged out by force by the system. You can re-activate
        the user at any time. -->
      </template>
      <div class="text-muted-foreground text-xs font-medium">
        Active since {{ formatDateTime(tenantUser.activeFrom) }}
      </div>
      <Button
        v-if="tenantUser.canDeactivate"
        variant="destructive"
        size="sm"
        class="w-fit"
        :disabled="isDeactivating"
        :data-testid="testIds.tenantUser.actions.deactivateUser"
        @click="modals.showDeactivate({ tenantUser })"
      >
        <IconLoading v-if="isDeactivating" class="size-4 shrink-0" />
        <IconDeactivate v-else class="size-4" />
        Deactivate User
      </Button>
    </ContentRowTwoColumns>
    <ContentRowTwoColumns
      v-if="!tenantUser.isActive"
      title="Schedule User Activation"
    >
      <template #description>
        Set a future date for the user to become active.
      </template>
      <div class="text-muted-foreground text-xs font-medium">
        <template v-if="TenantUserUtils.isScheduledForActivation(tenantUser)">
          Will be activated on
          {{ formatDateTime(tenantUser.activeFrom) }}
        </template>
        <template v-else> Not scheduled for activation. </template>
      </div>
      <Button
        v-if="!tenantUser.isActive"
        variant="outline"
        class="w-fit"
        :disabled="isActivating"
        :data-testid="testIds.tenantUser.actions.scheduleUserActivation"
        @click="modals.showScheduleActivation({ tenantUser })"
      >
        <IconLoading v-if="isActivating" class="size-4 shrink-0" />
        <IconCalendarClock v-else class="size-4" />
        Schedule User Activation
      </Button>
    </ContentRowTwoColumns>

    <ContentRowTwoColumns
      v-if="tenantUser.canScheduleDeactivation"
      title="Schedule User Deactivation"
    >
      <template #description>
        Plan ahead by setting a date when the user's account will be
        deactivated.
      </template>
      <div class="text-muted-foreground text-xs font-medium">
        <template v-if="TenantUserUtils.isScheduledForDeactivation(tenantUser)">
          Will be deactivated on
          {{ formatDateTime(tenantUser.activeTo) }}
        </template>
        <template v-else> Not scheduled for deactivation. </template>
      </div>
      <Button
        v-if="tenantUser.canScheduleDeactivation"
        variant="outline"
        class="w-fit"
        :disabled="isSchedulingDeactivation"
        :data-testid="testIds.tenantUser.actions.scheduleUserDeactivation"
        @click="modals.showScheduleDeactivation({ tenantUser })"
      >
        <IconLoading v-if="isSchedulingDeactivation" class="size-4 shrink-0" />
        <IconCalendarClock v-else class="size-4" />
        Schedule User Deactivation
      </Button>
    </ContentRowTwoColumns>
    <ContentRowTwoColumns
      v-if="
        tenantUser.canBeGrantedTenantAdminPrivileges ||
        tenantUser.canHaveTenantAdminPrivilegesRevoked
      "
      title="Tenant Admin Privileges"
    >
      <template #description>
        Control whether this user has full administrative access to the entire
        tenant. Assign with caution.
      </template>
      <div class="text-muted-foreground text-xs font-medium">
        {{
          tenantUser.isTenantAdmin
            ? 'This user is a tenant admin.'
            : 'This user is not a tenant admin.'
        }}
      </div>
      <Button
        v-if="tenantUser.canBeGrantedTenantAdminPrivileges"
        variant="outline"
        size="sm"
        class="w-fit"
        :disabled="isGranting"
        :data-testid="testIds.tenantUser.actions.grantTenantAdminPrivileges"
        @click="modals.showGrantTenantAdmin({ tenantUser })"
      >
        <IconLoading v-if="isGranting" class="size-4 shrink-0" />
        <IconAdmin v-else class="size-4" />
        Grant Tenant Admin Privileges
      </Button>
      <Button
        v-if="tenantUser.canHaveTenantAdminPrivilegesRevoked"
        variant="outline"
        size="sm"
        class="w-fit"
        :disabled="isRevoking"
        :data-testid="testIds.tenantUser.actions.revokeTenantAdminPrivileges"
        @click="modals.showRevokeTenantAdmin({ tenantUser })"
      >
        <IconLoading v-if="isRevoking" class="size-4 shrink-0" />
        <IconAdmin v-else class="size-4" />
        Revoke Tenant Admin Privileges
      </Button>
    </ContentRowTwoColumns>
  </div>
</template>
